import InfoCard from "../components/InfoCard.js";
import { AreaChartComponent } from "../components/AreaChartComponent.tsx";
import { PieChartComponent } from "../components/PieChartComponent.tsx";
import { dashboardService } from "../services/dashboardService.js";
import { Skeleton } from "../components/ui/skeleton.tsx";
import { useEffect, useState } from "react";
import { format, subDays } from "date-fns/esm";
import { GrGroup } from "react-icons/gr";
import { GoLocation, GoPeople } from "react-icons/go";
import { LuRecycle } from "react-icons/lu";

// Color palette for the pie chart
const MATERIAL_COLORS = [
  "#4285F4", // Blue
  "#FFD700", // Gold
  "#FF4500", // Orange
  "#0078D7", // Dark Blue
  "#34A853", // Green
  "#888888", // Gray (for others)
];

export default function Dashboard() {
  const [totalCommunities, setTotalCommunities] = useState(0);
  const [countRecycledMaterials, setCountRecycledMaterials] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPocs, setTotalPocs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [topMaterials, setTopMaterials] = useState([]);

  const [topCommunities, setTopCommunities] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const startDate = format(subDays(new Date(), 30), "yyyy-MM-dd");
        const endDate = format(new Date(), "yyyy-MM-dd");

        const [
          communitiesData,
          usersData,
          pocsData,
          recycledMaterialsResponse,
          chartResponse,
          communitiesResponse, // Add this new call
        ] = await Promise.all([
          dashboardService.totalCommunities(),
          dashboardService.totalUsers(),
          dashboardService.totalPocs(),
          dashboardService.countRecycledMaterials(),
          dashboardService.countRecycledItemsByDate(startDate, endDate),
          dashboardService.countCommunities(), // Call without dates to get all communities
        ]);

        setTotalCommunities(communitiesData[0]?.total || 0);
        setTotalUsers(usersData[0]?.total || 0);
        setTotalPocs(pocsData[0]?.total || 0);

        // Set total recycled points
        setCountRecycledMaterials(recycledMaterialsResponse?.total_points || 0);

        // Process the materials data for pie chart
        if (recycledMaterialsResponse?.data) {
          // Sort by total_quantities (descending) and take top 5
          const sortedMaterials = [...recycledMaterialsResponse.data]
            .sort((a, b) => b.total_quantities - a.total_quantities)
            .slice(0, 5);

          // Format for pie chart with colors
          const formattedMaterials = sortedMaterials.map((item, index) => ({
            materialItem: item.category,
            material: item.total_quantities,
            fill: MATERIAL_COLORS[index % MATERIAL_COLORS.length],
          }));

          setTopMaterials(formattedMaterials);
        }

        // Format area chart data
        const formattedChartData = chartResponse.map((item) => ({
          date: item._id,
          total_quantities: item.total_quantities,
          total_points: item.total_points,
        }));
        setChartData(formattedChartData);

        if (communitiesResponse) {
          // Sort by total_points (descending) and take top 5
          const sortedCommunities = [...communitiesResponse]
            .sort((a, b) => b.total_points - a.total_points)
            .slice(0, 5);

          // Format for pie chart with colors
          const formattedCommunities = sortedCommunities.map((item, index) => ({
            materialItem: item.name,
            material: item.total_points,
            fill: MATERIAL_COLORS[index % MATERIAL_COLORS.length],
          }));

          setTopCommunities(formattedCommunities);
        }
      } catch (err) {
        console.error(err);
        setError("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (error) return <p>{error}</p>;

  return (
    <div className="flex flex-col min-h-screen p-4">
      {/* Info Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Communities Card */}
        <InfoCard
          heading="Total Communities"
          number={
            loading ? (
              <Skeleton className="w-full h-8" /> // Skeleton for number
            ) : (
              totalCommunities.toLocaleString() || "0"
            )
          }
          icon={GrGroup}
          iconColor="text-indigo-500"
          bgColor="bg-indigo-100"
          bottomText="Updated Today"
        />

        {/* Total Point of Collection Card */}
        <InfoCard
          heading="Total Point of Collection"
          number={
            loading ? (
              <Skeleton className="w-full h-8" /> // Skeleton for number
            ) : (
              totalPocs.toLocaleString() || "0"
            )
          }
          icon={GoLocation}
          iconColor="text-lime-500"
          bgColor="bg-lime-100"
          bottomText="Updated Today"
        />

        {/* Total Users Card */}
        <InfoCard
          heading="Total Users"
          number={
            loading ? (
              <Skeleton className="w-full h-8" /> // Skeleton for number
            ) : (
              totalUsers.toLocaleString() || "0"
            )
          }
          icon={GoPeople}
          iconColor="text-teal-500"
          bgColor="bg-teal-100"
          bottomText="38.74% vs Last Week"
        />

        <InfoCard
          heading="Total Recycled Points"
          number={
            loading ? (
              <Skeleton className="w-full h-8" /> // Skeleton for number
            ) : (
              countRecycledMaterials.toLocaleString() || "0"
            )
          }
          icon={LuRecycle}
          iconColor="text-fuchsia-500"
          bgColor="bg-fuchsia-100"
          bottomText="2.5% vs Last Week"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <div className="col-span-1 md:col-span-2 lg:col-span-2">
          <AreaChartComponent data={chartData} loading={loading} />
        </div>

        <div className="col-span-1">
          <PieChartComponent
            title="Top Recycled Materials"
            description="By Quantity"
            data={loading ? [] : topMaterials}
            loading={loading}
            trendText=""
            footerText="Top 5 materials recycled"
            showTrendIcon={false}
          />
        </div>
        <div className="col-span-1">
          <PieChartComponent
            title="Top Communities"
            description="By Recycling Points"
            data={loading ? [] : topCommunities}
            loading={loading}
            centerLabel="Points"
            trendText=""
            footerText="Top 5 communities by recycling points"
            showTrendIcon={false}
          />
        </div>
      </div>
    </div>
  );
}
